import React from "react";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaModalIconWrapper, {
  type KuikaModalIconIntent
} from "../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import "./confirm-dialog.scss";

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  // Danger tints the icon + confirm button red (destructive actions like discard/delete).
  danger?: boolean;
  // Overrides the badge's meaning. Defaults to "destructive" when danger is set, "neutral" otherwise.
  intent?: KuikaModalIconIntent;
  // Overrides the badge's glyph. Defaults to a glyph that fits the resolved intent.
  iconName?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

// The badge's fallback glyph when a call site doesn't pass its own iconName — one that fits the
// resolved intent by default.
function defaultIconNameForIntent(intent: KuikaModalIconIntent): string {
  if (intent === "destructive") return "trash";
  if (intent === "warning") return "alert-triangle";
  return "info-circle";
}

// Reusable confirmation modal — replaces window.confirm for board prompts so they match the rest
// of the UI: a centered alert with a tinted glyph, a clear title/message, and balanced actions.
// hideHeader + the whole card in children gives full control over padding/layout.
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  danger = false,
  intent,
  iconName,
  onConfirm,
  onCancel
}) => {
  const resolvedIntent: KuikaModalIconIntent = intent ?? (danger ? "destructive" : "neutral");
  const resolvedIconName: string = iconName ?? defaultIconNameForIntent(resolvedIntent);

  return (
    <KModal showModal={open} closeModal={onCancel} centered width={400} hideHeader dontRenderSpinner>
      <div className="kuika-confirm">
        <div className="kuika-confirm__icon">
          <KuikaModalIconWrapper iconName={resolvedIconName} intent={resolvedIntent} />
        </div>
        <div className="kuika-confirm__title">{title}</div>
        <div className="kuika-confirm__message">{message}</div>
        <div className="kuika-confirm__actions">
          <KuikaButton type="tertiary" color="neutral" onClick={onCancel}>
            {cancelLabel}
          </KuikaButton>
          <KuikaButton
            type="primary"
            color={danger ? "danger" : "default"}
            onClick={onConfirm}
            data-testid="board-confirm-accept"
          >
            {confirmLabel}
          </KuikaButton>
        </div>
      </div>
    </KModal>
  );
};

export default ConfirmDialog;
